// Typ: FindHypotenuseRightTriangleTask
// Dział: Planimetria → Trójkąty → Trójkąt prostokątny
// Cel: Oblicz c = √(a² + b²) z uproszczeniem (np. √50 → 5√2)
// Wejście: RootFractionInput (jak w FindAreaHeronTask) + komunikat o uproszczeniu (jak w LengthTask)

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// ===== helpers „jak w LengthTask” =====
function evaluateExpression(expr) {
  const cleaned = String(expr ?? "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\\\sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/\\sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/sqrt\{([\d.,]+)\}/g, "√$1")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/,/g, ".");
  try {
    const parsed = cleaned
      .replace(/(\d*)√(\d+(\.\d+)?)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+(\.\d+)?)/g, (_, b) => `Math.sqrt(${b})`);
    return Function(`return ${parsed}`)();
  } catch {
    return NaN;
  }
}

function normalizeCanonical(str) {
  return String(str ?? "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\√/g, "√")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/^1√/, "√")
    .replace(/√0*(\d+)/g, (_, d) => `√${parseInt(d, 10)}`)
    .replace(/^0*(\d+)$/, (_, d) => `${parseInt(d, 10)}`)
    .trim();
}

export function simplifySqrt(n) {
  let factor = 1;
  for (let i = Math.floor(Math.sqrt(n)); i >= 2; i--) {
    if (n % (i * i) === 0) { factor = i; break; }
  }
  const outside = factor;
  const inside = n / (factor * factor);
  if (inside === 1) return `${outside}`;
  if (outside === 1) return `\\sqrt{${inside}}`;
  return `${outside}\\sqrt{${inside}}`;
}

// ===== generator =====
const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;
const TRIPLES = [
  [3, 4, 5], [5, 12, 13], [8, 15, 17], [7, 24, 25],
  [9, 40, 41], [12, 35, 37], [20, 21, 29]
];

export function generateTask() {
  // miks: częściej ogólne a,b (żeby wychodził √), czasem „ładna” trójka
  const useGeneral = Math.random() < 0.7;

  let a, b;
  if (useGeneral) {
    a = rnd(2, 15);
    b = rnd(2, 15);
  } else {
    const [x, y] = TRIPLES[rnd(0, TRIPLES.length - 1)];
    const k = rnd(1, 3);
    a = x * k; b = y * k;
  }

  const sum = a * a + b * b;
  const sqrtLatex = simplifySqrt(sum);

  return { type: "FindHypotenuseRightTriangleTask", data: { a, b, sum, sqrtLatex } };
}

// ===== render task =====
export function renderTask({ data }) {
  const { a, b } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest trójkąt prostokątny o przyprostokątnych długości{" "}
      <InlineMath math={`\\ a=${a}`} /> oraz <InlineMath math={`\\ b=${b}`} />.
      Oblicz długość przeciwprostokątnej <InlineMath math={'c'} />.
    </div>
  );
}

// ===== input — UWAGA: value to STRING (jak w FindAreaHeronTask) =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}             // ← string lub undefined; NIE obiekt
        onChange={onChange}
        disabled={disabled}
        label={'c'}
        placeholder="np. (5\\sqrt{2}), 10, 7\\tfrac{1}{2}, \\sqrt{50}, 5\\sqrt{2}"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  // 1) weź raw string z RootFractionInput (jak w Heronie)
  const raw = typeof userAnswer === 'object' ? userAnswer?.raw : userAnswer;
  if (!raw || typeof raw !== 'string' || raw.trim() === '') return false;

  // 2) wartościowo — akceptuj liczbowo równoważne (tolerancja jak w LengthTask)
  const expectedLatex = String(data.sqrtLatex || `\\sqrt{${data.sum}}`);
  const expectedNorm = normalizeCanonical(expectedLatex);
  const givenNorm = normalizeCanonical(raw);

  const expectedVal = evaluateExpression(expectedNorm);
  const givenVal = evaluateExpression(givenNorm);
  const isClose = Math.abs(expectedVal - givenVal) < 0.01;
  if (!isClose) {
    // fallback: jeśli RootFractionInput potrafi sparsować do liczby (jak w Heronie)
    const parsed = parseRootFractionInput(raw);
    if (!(Number.isFinite(parsed) && Math.abs(parsed - expectedVal) < 0.01)) return false;
  }

  // 3) forma — jeśli niekanoniczna, poproś o uproszczenie
  const strip = (s) => s.replace(/[.,]/g, "");
  if (strip(expectedNorm) === strip(givenNorm)) return true;

  return {
    correct: true,
    needsSimplification: true,
    expected: expectedLatex
  };
}

// ===== answer =====
export function renderAnswer({ data }) {
  const shown = String(data?.sqrtLatex ?? `\\sqrt{${data?.sum}}`);
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`c = ${shown}`} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a, b, sum, sqrtLatex } = data;
  const a2 = a * a, b2 = b * b;
  const justRoot = `\\sqrt{${sum}}`;
  const simplified = sqrtLatex || justRoot;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Twierdzenie Pitagorasa:</div>
      <BlockMath math={'c^2 = a^2 + b^2'} />

      <div><strong>Krok 2.</strong> Podstawiamy:</div>
      <BlockMath math={`c^2 = ${a}^2 + ${b}^2 = ${a2} + ${b2} = ${sum}`} />

      <div><strong>Krok 3.</strong> Pierwiastkujemy i upraszczamy:</div>
      {simplified === justRoot
        ? <BlockMath math={`c = ${justRoot}`} />
        : <BlockMath math={`c = ${justRoot} = ${simplified}`} />
      }
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === 'object' ? userAnswer?.raw : userAnswer;
  if (!raw || typeof raw !== 'string' || raw.trim() === '') return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, 'c')} />
    </div>
  );
}

// ===== default export =====
export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
